import { environment, environmentVariables } from './environmentVariables'
import { QueryParameters } from './queryParameters'

/**
 * Validates the `origin` query param and returns it only when it points at a
 * trusted Swile host (`*.swile.co`, or `localhost` in dev). Returns `null` for
 * a missing, malformed or untrusted origin.
 *
 * Single shared implementation for both login (`SignInRoute`) and the BR
 * activation funnel (`activationRedirectUrl`) — do NOT fork it.
 */
export const originParamUrl = (searchParams: URLSearchParams): string | null => {
  const originParam = searchParams.get(QueryParameters.ORIGIN)
  try {
    if (originParam) {
      // Exact host match, NOT a suffix: `endsWith('swile.co')` would also accept
      // attacker-registrable domains like `evilswile.co`. Only `swile.co` itself
      // and its subdomains (`*.swile.co`) are trusted (+ `localhost` in dev).
      const { hostname } = new URL(originParam)
      if (
        hostname === 'swile.co' ||
        hostname.endsWith('.swile.co') ||
        (environment === 'development' && hostname === 'localhost')
      ) {
        return originParam
      }

      console.error(`Trying to redirect to unauthorized url: '${originParam}'`)
    }
  } catch (typeError) {
    console.error(`Trying to redirect to invalid url: '${originParam}'`)
  }
  return null
}

/**
 * Post-activation destination (contract pack §12, FE-owned). Mirrors login's
 * `SignInRoute#redirectionAfterSignInUrl` MINUS the affiliate branch: BR-activated
 * users are ALWAYS employees (product-confirmed 2026-06-10), so no `isAffiliate`
 * lookup and no OAuth access token are needed during the funnel.
 *
 * Resolution: validated `origin` query param ELSE `EMPLOYEE_APP_URL`. Never null.
 */
export const activationRedirectUrl = (searchParams: URLSearchParams): string =>
  originParamUrl(searchParams) ?? environmentVariables('EMPLOYEE_APP_URL')
